import createClient from 'openapi-fetch';

import type { components, paths } from './schema.js';

export type Schemas = components['schemas'];

/** Problem details (RFC 9457) as the API sends them (api-spec §2). */
export interface Problem {
  readonly code: string;
  readonly status: number;
  readonly title?: string;
  readonly detail?: string;
  readonly errors?: readonly { path: string; message: string }[];
  readonly limit?: { name: string; max?: number; min?: number; current?: number };
  readonly traceId?: string;
}

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail ?? problem.title ?? problem.code);
  }

  get code() {
    return this.problem.code;
  }

  /** Message of the first validation error for a field, if the API named one. */
  fieldError(path: string): string | undefined {
    return this.problem.errors?.find((e) => e.path === path)?.message;
  }
}

export const api = createClient<paths>({
  baseUrl: '/api/v1',
  credentials: 'include',
  // The API's CSRF check (architecture §10.3) requires this header on every change.
  headers: { 'X-Requested-With': 'fetch' },
});

const asProblem = (error: unknown, status: number): Problem =>
  error && typeof error === 'object' && 'code' in error
    ? (error as Problem)
    : { code: status === 0 ? 'NETWORK' : 'INTERNAL', status };

/** Unwraps an openapi-fetch result: the data, or an ApiError with the problem details. */
export async function unwrap<T>(
  request: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let result: Awaited<typeof request>;
  try {
    result = await request;
  } catch {
    throw new ApiError({ code: 'NETWORK', status: 0 });
  }
  if (result.error !== undefined || !result.response.ok) {
    throw new ApiError(asProblem(result.error, result.response.status));
  }
  return result.data as T;
}
